import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Share, Plus, X, MoreVertical, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const ua = () => window.navigator.userAgent || "";
const isIOS = () => /iphone|ipad|ipod/i.test(ua()) || (/Macintosh/.test(ua()) && navigator.maxTouchPoints > 1);
const isInApp = () => /FBAN|FBAV|Instagram|WhatsApp|Line\/|Telegram|GSA\//i.test(ua());

const Step = ({ n, children }) => (
  <li className="flex items-center gap-3">
    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#E63946] text-white text-xs font-bold shrink-0">{n}</span>
    <span className="flex flex-wrap items-center gap-1.5">{children}</span>
  </li>
);

const Guide = ({ ios, inApp }) => {
  if (inApp) return (
    <>
      <h3 className="text-lg font-bold mb-2">Apri il sito nel browser</h3>
      <p className="text-sm text-muted-foreground mb-4">Da WhatsApp, Facebook o Instagram l'app non si può installare.</p>
      <ol className="space-y-4 text-sm text-muted-foreground">
        <Step n={1}>Tocca <MoreVertical className="w-4 h-4 text-foreground" /> o <Share className="w-4 h-4 text-foreground" /> in alto o in basso</Step>
        <Step n={2}>Scegli <strong className="text-foreground">Apri in {ios ? "Safari" : "Chrome"}</strong> <Compass className="w-4 h-4 text-foreground" /></Step>
        <Step n={3}>Poi tocca di nuovo <strong className="text-foreground">Installa App</strong></Step>
      </ol>
    </>
  );
  if (ios) return (
    <>
      <h3 className="text-lg font-bold mb-4">Installa l'app su iPhone/iPad</h3>
      <ol className="space-y-4 text-sm text-muted-foreground">
        <Step n={1}>Tocca <Share className="w-4 h-4 text-foreground" /> <strong className="text-foreground">Condividi</strong> nella barra di Safari (se non lo vedi, tocca prima <strong className="text-foreground">⋯</strong>)</Step>
        <Step n={2}>Scorri e scegli <Plus className="w-4 h-4 text-foreground" /> <strong className="text-foreground">Aggiungi alla schermata Home</strong></Step>
        <Step n={3}>Tocca <strong className="text-foreground">Aggiungi</strong> in alto a destra</Step>
      </ol>
    </>
  );
  return (
    <>
      <h3 className="text-lg font-bold mb-4">Installa l'app sul telefono</h3>
      <ol className="space-y-4 text-sm text-muted-foreground">
        <Step n={1}>Tocca <MoreVertical className="w-4 h-4 text-foreground" /> in alto a destra del browser</Step>
        <Step n={2}>Scegli <strong className="text-foreground">Installa app</strong> oppure <strong className="text-foreground">Aggiungi a schermata Home</strong></Step>
        <Step n={3}>Conferma con <strong className="text-foreground">Installa</strong></Step>
      </ol>
    </>
  );
};

export const InstallAppButton = ({ className = "", block = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(window.__installPrompt || null);
  const [installed, setInstalled] = useState(isStandalone());
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    const onBefore = (e) => { e.preventDefault(); window.__installPrompt = e; setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onBefore); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (installed) return null;

  const click = async () => {
    if (deferredPrompt && !isInApp()) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      window.__installPrompt = null;
      setDeferredPrompt(null);
    } else setGuide(true);
  };

  return (
    <>
      <Button onClick={click} variant="outline" data-testid="install-app-btn"
        className={`rounded-full border-[#E63946]/40 text-foreground hover:bg-[#E63946]/10 px-4 font-semibold gap-2 ${block ? "w-full h-12" : ""} ${className}`}>
        <Download className="w-4 h-4" />Installa App
      </Button>
      {guide && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={() => setGuide(false)} data-testid="install-ios-modal">
          <div className="glass border-t border-white/10 rounded-t-3xl w-full max-w-md p-6 relative animate-in slide-in-from-bottom duration-300 max-h-[80vh] overflow-y-auto"
            style={{ paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))" }} onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/20" />
            <button onClick={() => setGuide(false)} className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-muted-foreground hover:text-foreground" aria-label="Chiudi" data-testid="install-ios-close"><X className="w-5 h-5" /></button>
            <Guide ios={isIOS()} inApp={isInApp()} />
          </div>
        </div>, document.body
      )}
    </>
  );
};
